import "./resultSeriesStyle.scss";
import { useMantineColorScheme } from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { useTranslation } from "react-i18next";
import { formatEnUsValue, formatProjectDateTime, formatProjectNumber, useCurrency } from "../../utils/helpers";
import {
  fontFamily,
  stepMs,
  SERIES_CONFIG,
  SOC_CONFIG,
  leftIndex,
  rightIndex,
  nearestIndex,
  nearestRenderedPoint,
  toSocPercent,
} from "./chartHelpers";

const rootScope = typeof window !== "undefined" ? window : {};

if (rootScope) {
  const cssObj = rootScope.CSS || {};
  if (typeof cssObj.supports !== "function") {
    cssObj.supports = () => false;
  }
  if (!rootScope.CSS) {
    rootScope.CSS = cssObj;
  }
}

const highstockModule = require("highcharts/highstock");
const Highcharts = highstockModule.default || highstockModule;

const sameDomain = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Math.abs(a[0].getTime() - b[0].getTime()) < 1 && Math.abs(a[1].getTime() - b[1].getTime()) < 1;
};

const createDateAxisFormatter = () => function () {
  const spanMs = (this.axis.max || 0) - (this.axis.min || 0);
  const dayMs = 24 * 60 * 60 * 1000;
  if (spanMs <= 16 * dayMs) return moment(this.value).format("D MMM YYYY H:mm");
  if (spanMs <= 120 * dayMs) return moment(this.value).format("D MMM YYYY");
  return moment(this.value).format("MMM YYYY");
};

const formatPowerAxisLabel = function () {
  const value = this.value;
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  return formatProjectNumber(value);
};

const formatSocAxisLabel = function () {
  return `${Math.round(this.value)}%`;
};

const createTooltipFormatter = ({
  chartRef,
  timesMs,
  series,
  socSeries,
  scaleSeriesValue,
  unitHelpers,
}) => function () {
  // Snap to the nearest rendered (grouped) point's pt.x, which equals
  // the FIRST raw timestamp of the group, then read the raw value at
  // that index. This matches CSV output at every zoom level.
  const chart = chartRef.current?.chart;
  const cursorX = (chart?.pointer?.chartX != null && chart?.xAxis?.[0])
    ? chart.xAxis[0].toValue(chart.pointer.chartX)
    : this.x;

  // Find nearest rendered point across all visible series for stable snapping.
  let snappedX = cursorX;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const chartSeries of (chart?.series || [])) {
    if (!chartSeries.visible || !chartSeries.points?.length) continue;
    const point = nearestRenderedPoint(chartSeries.points, cursorX);
    if (!point || !Number.isFinite(point.x)) continue;
    const dist = Math.abs(point.x - cursorX);
    if (dist < bestDist) {
      bestDist = dist;
      snappedX = point.x;
    }
  }
  const idx = nearestIndex(timesMs, snappedX);
  const displayX = idx >= 0 ? timesMs[idx] : cursorX;
  const dt = formatProjectDateTime(displayX);
  let html = `<strong>${dt}</strong><br/>`;

  // Always show all currently visible series, even if downsampled points don't share the same x.
  const chartSeriesById = new Map();
  (chart?.series || []).forEach((chartSeries) => {
    const id = chartSeries?.options?.id;
    if (!id || !chartSeries.visible) return;
    chartSeriesById.set(id, chartSeries);
  });

  const orderedSeries = [
    ...series.map((item) => ({ key: item.key, label: item.label, color: item.color, isSoc: false })),
    ...(socSeries ? [{ key: socSeries.key, label: socSeries.label, color: socSeries.color, isSoc: true }] : []),
  ];

  orderedSeries.forEach((entry) => {
    const plotted = chartSeriesById.get(entry.key);
    if (!plotted) return;

    let rawVal = null;
    if (idx >= 0) {
      if (entry.isSoc && socSeries) {
        const value = toSocPercent(socSeries.values[Math.min(idx, socSeries.values.length - 1)]);
        rawVal = Number.isFinite(value) ? value : null;
      } else {
        const matched = series.find((item) => item.key === entry.key);
        if (matched) {
          rawVal = scaleSeriesValue(matched, matched.values[Math.min(idx, matched.values.length - 1)]);
        }
      }
    }

    if (!Number.isFinite(rawVal)) return;
    const valStr = formatEnUsValue(rawVal);
    const unit = entry.isSoc ? "%" : unitHelpers.unitLabel;
    const color = plotted.color || entry.color;
    const name = plotted.name || entry.label;
    html += `<span style="color:${color}">&#9679;</span> ${name}: <b>${valStr}</b> ${unit}<br/>`;
  });

  return html;
};

const createTooltipPositioner = () => function (labelWidth, _labelHeight, point) {
  const chart = this.chart;
  const cursorX = chart?.pointer?.chartX ?? (chart.plotLeft + point.plotX);
  const offset = 20;
  const plotRight = chart.plotLeft + chart.plotWidth;
  const x = cursorX + offset + labelWidth > plotRight
    ? cursorX - labelWidth - offset
    : cursorX + offset;
  return { x: Math.max(0, x), y: chart.plotTop + 8 };
};

const ResultSeries = ({
  results,
  onFullscreen,
  kpiProjectSettings,
  inModalInstance = false,
  legendGap = 16,
  // Allow callers to pass custom series config (e.g. thermal) instead of the default electrical config
  seriesConfig = SERIES_CONFIG,
  socConfig    = SOC_CONFIG,
  unitSuffix   = "",  // e.g. "_th" for thermal units
  downsamplePointsPerDay = 3,
}) => {
  const { t } = useTranslation();
  const currency = useCurrency();
  const { colorScheme } = useMantineColorScheme();

  const chartRef = useRef(null);

  // Fix: navigator drag stays stuck when mouse is released outside the browser window.
  // mousemove with e.buttons===0 catches stale drag that a missed mouseup leaves behind.
  useEffect(() => {
    const releaseNavigator = (e) => {
      // On mousemove, only act when no button is held (drag already ended)
      if (e.type === "mousemove" && e.buttons !== 0) return;
      const chart = chartRef.current?.chart;
      if (!chart) return;
      // 1. Standard Highcharts pointer release
      try { chart.pointer?.onDocumentMouseUp(e); } catch (_) {}
      // 2. Directly clear navigator grabbed flags (covers all HC v12 variants)
      try {
        const nav = chart.navigator;
        if (nav) {
          nav.grabbedLeft   = false;
          nav.grabbedRight  = false;
          nav.grabbedCenter = false;
          nav.hasDragged    = false;
        }
      } catch (_) {}
      // 3. Clear pointer-level drag state
      try {
        if (chart.pointer) {
          chart.pointer.isDragging    = false;
          chart.pointer.hasDragged    = false;
          chart.pointer.selectionMarker = null;
        }
      } catch (_) {}
    };
    document.addEventListener("mouseup",   releaseNavigator);
    document.addEventListener("mousemove", releaseNavigator);
    return () => {
      document.removeEventListener("mouseup",   releaseNavigator);
      document.removeEventListener("mousemove", releaseNavigator);
    };
  }, []);
  const [hidden, setHidden] = useState(new Set());
  const [xDomain, setXDomain] = useState(null);
  const SHADING_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;
  const isShadingVisible = xDomain != null &&
    (xDomain[1].getTime() - xDomain[0].getTime()) <= SHADING_THRESHOLD_MS;
  const [panMode, setPanMode] = useState(false);
  const [powerUnit, setPowerUnit] = useState("W");
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [pvTransformEnabled, setPvTransformEnabled] = useState(false);

  const unitHelpers = useMemo(() => {
    const unitFactor = powerUnit === "kW" ? 1 / 1000 : 1;
    const unitLabel = powerUnit + unitSuffix;
    const scaleValue = (v) => {
      const num = +v;
      return Number.isFinite(num) ? num * unitFactor : null;
    };
    const powerAxisLabel = powerUnit === "kW" ? t("timeSeries.axis.powerKW") : t("timeSeries.axis.powerW");
    return { unitFactor, unitLabel, scaleValue, powerAxisLabel };
  }, [powerUnit, unitSuffix, t]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === "function") onFullscreen();
  }, [onFullscreen]);

  const series = useMemo(() => {
    const r = results || {};
    const lowerMap = Object.keys(r).reduce((acc, k) => {
      acc[k.toLowerCase()] = k;
      return acc;
    }, {});
    const picked = [];
    seriesConfig.forEach((cfg) => {
      const match = cfg.labels.find((l) => lowerMap[l]);
      if (!match) return;
      const sourceKey = lowerMap[match];
      const raw = r[sourceKey];
      if (Array.isArray(raw) && raw.length) {
        picked.push({
          key: cfg.id,
          sourceKey,
          label: cfg.label,
          color: cfg.color,
          values: raw,
        });
      }
    });
    return picked;
  }, [results, seriesConfig]);

  const socSeries = useMemo(() => {
    if (!socConfig) return null;
    const r = results || {};
    const lowerMap = Object.keys(r).reduce((acc, k) => {
      acc[k.toLowerCase()] = k;
      return acc;
    }, {});
    const match = socConfig.labels.find((l) => lowerMap[l]);
    if (!match) return null;
    const sourceKey = lowerMap[match];
    const raw = r[sourceKey];
    if (!Array.isArray(raw) || !raw.length) return null;
    return {
      key: socConfig.id,
      sourceKey,
      label: socConfig.label,
      color: socConfig.color,
      values: raw,
    };
  }, [results, socConfig]);

  const isPvLikeSeries = useCallback((seriesMeta) => {
    const text = `${seriesMeta?.label || ""} ${seriesMeta?.sourceKey || ""} ${seriesMeta?.key || ""}`.toLowerCase();
    return text.includes("pv") || text.includes("solar");
  }, []);

  const hasPvSeries = useMemo(
    () => series.some((s) => isPvLikeSeries(s)),
    [series, isPvLikeSeries]
  );

  useEffect(() => {
    if (!hasPvSeries) setPvTransformEnabled(false);
  }, [hasPvSeries]);

  const scaleSeriesValue = useCallback((seriesMeta, rawValue) => {
    const scaled = unitHelpers.scaleValue(rawValue);
    if (!Number.isFinite(scaled)) return scaled;
    if (pvTransformEnabled && isPvLikeSeries(seriesMeta)) {
      return Math.abs(scaled);
    }
    return scaled;
  }, [unitHelpers, pvTransformEnabled, isPvLikeSeries]);

  const maxLen = useMemo(() => {
    const lengths = series.map((s) => s.values.length);
    if (socSeries?.values?.length) lengths.push(socSeries.values.length);
    return lengths.length ? Math.max(...lengths) : 0;
  }, [series, socSeries]);

  const startDate = useMemo(() => {
    if (!maxLen) return null;
    if (kpiProjectSettings?.startYear) {
      return new Date(kpiProjectSettings.startYear, 0, 1, 0, 0, 0, 0);
    }
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [maxLen, kpiProjectSettings]);

  const times = useMemo(() => {
    if (!maxLen || !startDate) return [];
    const base = startDate.getTime();
    const arr = new Array(maxLen);
    for (let i = 0; i < maxLen; i++) arr[i] = new Date(base + i * stepMs);
    return arr;
  }, [maxLen, startDate]);

  const timesMs = useMemo(() => times.map((d) => d.getTime()), [times]);

  const windowIndices = useMemo(() => {
    if (!timesMs.length) return { startIdx: 0, endIdx: -1 };
    if (!xDomain) return { startIdx: 0, endIdx: timesMs.length - 1 };
    const startMs = xDomain[0].getTime();
    const endMs = xDomain[1].getTime();
    const startIdx = Math.max(0, leftIndex(timesMs, startMs) - 1);
    const endIdx = Math.min(timesMs.length - 1, rightIndex(timesMs, endMs) - 1 + 1);
    return { startIdx, endIdx };
  }, [timesMs, xDomain]);

  // yRange only considers currently visible (non-hidden) series, so the axis
  // tightens when some legend items are toggled off.
  const yRange = useMemo(() => {
    const visibleSeries = series.filter((s) => !hidden.has(s.key));
    let globalMax = Number.NEGATIVE_INFINITY;
    let globalMin = Number.POSITIVE_INFINITY;
    for (const s of visibleSeries) {
      for (let i = 0; i < s.values.length; i++) {
        const num = scaleSeriesValue(s, s.values[i]);
        if (!Number.isFinite(num)) continue;
        if (num > globalMax) globalMax = num;
        if (num < globalMin) globalMin = num;
      }
    }
    if (!Number.isFinite(globalMax)) {
      globalMax = 1;
      globalMin = 0;
    }

    let windowMax = Number.NEGATIVE_INFINITY;
    let windowMin = Number.POSITIVE_INFINITY;
    for (const s of visibleSeries) {
      for (let i = windowIndices.startIdx; i <= windowIndices.endIdx && i < s.values.length; i++) {
        const num = scaleSeriesValue(s, s.values[i]);
        if (!Number.isFinite(num)) continue;
        if (num > windowMax) windowMax = num;
        if (num < windowMin) windowMin = num;
      }
    }
    if (!Number.isFinite(windowMax)) {
      windowMax = globalMax;
      windowMin = globalMin;
    }

    let min = xDomain ? windowMin : globalMin;
    let max = xDomain ? windowMax : globalMax;
    const forcedZeroFloor = min > 0;
    if (forcedZeroFloor) min = 0;
    if (max === min) {
      if (max === 0) max = 1;
      else min = 0;
    }
    // Pad top/bottom so peak values get headroom instead of touching the plot edge.
    const span = max - min;
    const pad = span * 0.05;
    if (!forcedZeroFloor) min -= pad;
    max += pad;

    // Round the tick step to a "nice" number (1/2/5 x 10^n) so a labeled
    // gridline lands near the data max (e.g. 200,000) instead of stopping short.
    const rawStep = (max - min) / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(rawStep, 1e-9))));
    const normalized = rawStep / magnitude;
    let niceNormalized;
    if (normalized <= 1) niceNormalized = 1;
    else if (normalized <= 2) niceNormalized = 2;
    else if (normalized <= 5) niceNormalized = 5;
    else niceNormalized = 10;
    const tickInterval = niceNormalized * magnitude;

    const niceMax = Math.ceil(max / tickInterval) * tickInterval;
    const niceMin = forcedZeroFloor ? 0 : Math.floor(min / tickInterval) * tickInterval;

    return { min: niceMin, max: niceMax, tickInterval };
  }, [series, windowIndices, xDomain, hidden, scaleSeriesValue]);

  const legendStats = useMemo(() => {
    const { unitLabel } = unitHelpers;
    const items = series.map((s) => {
      let wMin = Infinity;
      let wMax = -Infinity;
      for (let i = windowIndices.startIdx; i <= windowIndices.endIdx && i < s.values.length; i++) {
        const num = scaleSeriesValue(s, s.values[i]);
        if (!Number.isFinite(num)) continue;
        if (num < wMin) wMin = num;
        if (num > wMax) wMax = num;
      }
      return {
        key: s.key,
        label: s.label,
        color: s.color,
        hidden: hidden.has(s.key),
        minText: Number.isFinite(wMin) ? formatEnUsValue(wMin) : "-",
        maxText: Number.isFinite(wMax) ? formatEnUsValue(wMax) : "-",
        unitLabel,
      };
    });
    if (socSeries) {
      let wMin = Infinity;
      let wMax = -Infinity;
      for (let i = windowIndices.startIdx; i <= windowIndices.endIdx && i < socSeries.values.length; i++) {
        const num = toSocPercent(socSeries.values[i]);
        if (!Number.isFinite(num)) continue;
        if (num < wMin) wMin = num;
        if (num > wMax) wMax = num;
      }
      items.push({
        key: socSeries.key,
        label: socSeries.label,
        color: socSeries.color,
        hidden: hidden.has(socSeries.key),
        minText: Number.isFinite(wMin) ? formatEnUsValue(wMin) : "-",
        maxText: Number.isFinite(wMax) ? formatEnUsValue(wMax) : "-",
        unitLabel: "%",
      });
    }
    return items;
  }, [hidden, series, socSeries, unitHelpers, windowIndices, scaleSeriesValue]);

  const highchartsSeries = useMemo(() => {
    // Keep representative points/day while preserving key extremes.
    // Default count is configurable via `downsamplePointsPerDay`.
    const downsamplePerDay = (timesArr, dataArr, pointsPerDay) => {
      const result = [];
      const len = Math.min(timesArr.length, dataArr.length);
      const count = Math.max(2, Math.floor(Number(pointsPerDay) || 4));
      let i = 0;

      while (i < len) {
        const d = new Date(timesArr[i]);
        const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        let j = i;
        while (j < len) {
          const dj = new Date(timesArr[j]);
          const curDay = new Date(dj.getFullYear(), dj.getMonth(), dj.getDate()).getTime();
          if (curDay !== dayKey) break;
          j += 1;
        }

        const dayLen = j - i;
        if (dayLen > 0) {
          let minVal = Infinity;
          let maxVal = -Infinity;
          let minIdx = i;
          let maxIdx = i;

          for (let k = i; k < j; k++) {
            const value = dataArr[k];
            if (!Number.isFinite(value)) continue;
            if (value < minVal) {
              minVal = value;
              minIdx = k;
            }
            if (value > maxVal) {
              maxVal = value;
              maxIdx = k;
            }
          }

          const picked = new Set([i, j - 1, minIdx, maxIdx]);

          // Add evenly spaced anchors until reaching requested points/day.
          if (count > picked.size) {
            const interiorSlots = Math.max(0, count - picked.size);
            for (let n = 1; n <= interiorSlots; n += 1) {
              const ratio = n / (interiorSlots + 1);
              const idx = i + Math.floor((dayLen - 1) * ratio);
              picked.add(Math.max(i, Math.min(j - 1, idx)));
            }
          }

          // If duplicates reduced unique picks, fill with sequential points within day.
          if (picked.size < count) {
            for (let idx = i; idx < j && picked.size < count; idx += 1) {
              picked.add(idx);
            }
          }

          Array.from(picked)
            .sort((a, b) => a - b)
            .forEach((idx) => {
              result.push([timesArr[idx], dataArr[idx]]);
            });
        }

        i = j;
      }

      return result;
    };

    const useDownsampled = !xDomain;

    const seriesItems = series.map((s) => {
      const rawData = [];
      const len = Math.min(timesMs.length, s.values.length);
      for (let i = 0; i < len; i++) {
        rawData.push([timesMs[i], scaleSeriesValue(s, s.values[i])]);
      }
      const data = useDownsampled
        ? downsamplePerDay(timesMs.slice(0, len), rawData.map((p) => p[1]), downsamplePointsPerDay)
        : rawData;
      return {
        type: isShadingVisible ? "area" : "line",
        id: s.key,
        name: s.label,
        color: s.color,
        lineWidth: 1.4,
        fillColor: isShadingVisible ? {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, Highcharts.color(s.color).setOpacity(0.28).get("rgba")],
            [1, Highcharts.color(s.color).setOpacity(0).get("rgba")],
          ],
        } : undefined,
        yAxis: 0,
        data,
        visible: true, // visibility is applied imperatively in the hidden useEffect below
        showInNavigator: true,
        navigatorOptions: { color: s.color, lineWidth: 1 },
        dataGrouping: useDownsampled
          ? { enabled: false }
          : { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 2,
              fillColor: "#eb7857",
              lineColor: colorScheme === "dark" ? "#ffffff" : "#222222",
              lineWidth: 1.2,
            },
          },
        },
        states: {
          hover: { halo: { size: 0 }, lineWidth: 1.4, lineWidthPlus: 0, brightness: 0 },
          inactive: { opacity: 1 },
        },
      };
    });
    if (socSeries) {
      const rawData = [];
      const len = Math.min(timesMs.length, socSeries.values.length);
      for (let i = 0; i < len; i++) {
        const v = toSocPercent(socSeries.values[i]);
        rawData.push([timesMs[i], Number.isFinite(v) ? v : null]);
      }
      const data = useDownsampled
        ? downsamplePerDay(timesMs.slice(0, len), rawData.map((p) => p[1]), downsamplePointsPerDay)
        : rawData;
      seriesItems.push({
        type: "line",
        id: socSeries.key,
        name: socSeries.label,
        color: socSeries.color,
        lineWidth: 1.4,
        yAxis: 1,
        data,
        visible: true, // visibility controlled imperatively
        showInNavigator: true,
        navigatorOptions: { color: socSeries.color, lineWidth: 1 },
        dataGrouping: useDownsampled
          ? { enabled: false }
          : { enabled: true, groupPixelWidth: 2, approximation: "high", smoothed: false },
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 2,
              fillColor: "#eb7857",
              lineColor: colorScheme === "dark" ? "#ffffff" : "#222222",
              lineWidth: 1.2,
            },
          },
        },
        states: {
          hover: { halo: { size: 0 }, lineWidth: 1.4, lineWidthPlus: 0, brightness: 0 },
          inactive: { opacity: 1 },
        },
        tooltip: { valueSuffix: " %" },
      });
    }
    return seriesItems;
  }, [series, socSeries, timesMs, colorScheme, isShadingVisible, xDomain, downsamplePointsPerDay, scaleSeriesValue]); // 'hidden' intentionally omitted — visibility applied imperatively below

  // Apply legend visibility imperatively so toggling hidden never rebuilds 35k-point data arrays.
  // Re-runs on highchartsSeries changes too, since a full series rebuild (zoom/unit change)
  // resets every series back to visible:true and would otherwise silently undo hidden state.
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart?.series) return;
    let changed = false;
    chart.series.forEach((s) => {
      const id = s.options?.id;
      if (!id) return;
      const shouldBeVisible = !hidden.has(id);
      if (s.visible !== shouldBeVisible) {
        s.setVisible(shouldBeVisible, false); // false = defer redraw
        changed = true;
      }
    });
    if (changed) chart.redraw(false);
  }, [hidden, highchartsSeries]);

  const chartOptions = useMemo(() => {
    const bg = colorScheme === "dark" ? "#111111" : "#ffffff";
    const axisText = colorScheme === "dark" ? "#d5d7e0" : "#495057";
    const grid = colorScheme === "dark" ? "#2f2f2f" : "#e9ecef";
    const mask = colorScheme === "dark" ? "rgba(80,160,255,0.18)" : "rgba(0,123,255,0.13)";
    const outline = colorScheme === "dark" ? "#4da3ff" : "#007bff";

    const xMin = xDomain ? xDomain[0].getTime() : null;
    const xMax = xDomain ? xDomain[1].getTime() : null;

    return {
      chart: {
        backgroundColor: bg,
        height: 480,
        animation: false,
        spacingTop: Math.max(8, legendGap),
        style: { fontFamily },
        panning: {
          enabled: panMode,
          type: "x",
        },
        zooming: {
          type: panMode ? undefined : "x",
          mouseWheel: { enabled: false },
        },
      },
      credits: { enabled: false },
      title: { text: null },
      rangeSelector: { enabled: false },
      scrollbar: { enabled: false },
      legend: { enabled: false },
      navigator: {
        enabled: true,
        height: 48,
        margin: 8,
        maskFill: mask,
        outlineColor: outline,
        handles: {
          backgroundColor: outline,
          borderColor: outline,
        },
        xAxis: {
          labels: { enabled: false },
          gridLineWidth: 0,
        },
        yAxis: {
          gridLineWidth: 0,
        },
        series: {
          type: "line",
          lineWidth: 1,
        },
      },
      xAxis: {
        type: "datetime",
        crosshair: {
          color: colorScheme === "dark" ? "#888" : "#666",
          dashStyle: "Dash",
          width: 1,
          label: { enabled: false },
        },
        min: xMin,
        max: xMax,
        lineColor: grid,
        tickColor: grid,
        labels: {
          style: { color: axisText, fontSize: "11px" },
          formatter: createDateAxisFormatter(),
        },
        events: {
          setExtremes(e) {
            // Skip React state update during live navigator drag (mousemove).
            // Updating state triggers chartOptions → chart.update() which fights
            // with the navigator and resets its position mid-drag.
            if (e.DOMEvent?.type === "mousemove") return;
            if (typeof e.min !== "number" || typeof e.max !== "number") {
              if (xDomain !== null) setXDomain(null);
              return;
            }
            const next = [new Date(e.min), new Date(e.max)];
            if (!sameDomain(xDomain, next)) setXDomain(next);
          },
        },
      },
      yAxis: [
        {
          title: {
            text: unitHelpers.powerAxisLabel,
            style: { color: axisText, fontSize: "12px" },
          },
          min: yRange.min,
          max: yRange.max,
          tickInterval: yRange.tickInterval,
          startOnTick: false,
          endOnTick: false,
          opposite: false,
          gridLineColor: grid,
          labels: {
            style: { color: axisText, fontSize: "11px" },
            formatter: formatPowerAxisLabel,
          },
          plotLines:
            yRange.min < 0
              ? [{ value: 0, color: colorScheme === "dark" ? "#555" : "#999", dashStyle: "Dash", width: 1, zIndex: 2 }]
              : [],
        },
        {
          title: {
            text: "Battery SoC (%)",
            style: { color: axisText, fontSize: "12px" },
            margin: 14,
          },
          min: 0,
          max: 100,
          startOnTick: false,
          endOnTick: false,
          opposite: true,
          gridLineWidth: 0,
          labels: {
            style: { color: axisText, fontSize: "11px" },
            formatter: formatSocAxisLabel,
          },
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        borderRadius: 4,
        backgroundColor: colorScheme === "dark" ? "#222" : "#fff",
        borderColor: colorScheme === "dark" ? "#555" : "#666",
        style: {
          fontSize: "12px",
          color: colorScheme === "dark" ? "#d5d7e0" : "#222222",
        },
        formatter: createTooltipFormatter({
          chartRef,
          timesMs,
          series,
          socSeries,
          scaleSeriesValue,
          unitHelpers,
        }),
        // Keep tooltip at fixed Y (plot-top + 8) and follow crosshair X.
        // Flips to left side near the right edge so it never overlaps data points.
        positioner: createTooltipPositioner(),
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            hover: {
              halo: { size: 0 },
              lineWidthPlus: 0,
              brightness: 0,
            },
            inactive: { opacity: 1 },
          },
          turboThreshold: 0,
          stickyTracking: true,
        },
      },
      series: highchartsSeries,
    };
  }, [colorScheme, highchartsSeries, legendGap, panMode, series, socSeries, timesMs, unitHelpers, xDomain, yRange, scaleSeriesValue]);

  const getChart = () => chartRef.current?.chart;

  const countPointsInRange = (startMs, endMs) => {
    if (!timesMs.length) return 0;
    const i0 = leftIndex(timesMs, startMs);
    const i1 = rightIndex(timesMs, endMs) - 1;
    if (i1 < i0) return 0;
    return i1 - i0 + 1;
  };

  const applyExtremes = (startMs, endMs) => {
    const axis = getChart()?.xAxis?.[0];
    if (!axis) return;
    axis.setExtremes(startMs, endMs, true, false);
  };

  const handleZoomIn = () => {
    if (!timesMs.length) return;
    const fullStart = timesMs[0];
    const fullEnd = timesMs[timesMs.length - 1];
    const fullSpan = fullEnd - fullStart;

    const currentStart = xDomain ? xDomain[0].getTime() : fullStart;
    const currentEnd = xDomain ? xDomain[1].getTime() : fullEnd;
    const currentSpan = currentEnd - currentStart;

    const mid = (currentStart + currentEnd) / 2;
    const minSpan = stepMs * 4;
    const newSpan = Math.max(minSpan, currentSpan * 0.5);

    let nextStart = mid - newSpan / 2;
    let nextEnd = mid + newSpan / 2;
    if (nextStart < fullStart) {
      nextEnd += fullStart - nextStart;
      nextStart = fullStart;
    }
    if (nextEnd > fullEnd) {
      nextStart -= nextEnd - fullEnd;
      nextEnd = fullEnd;
    }

    if (countPointsInRange(nextStart, nextEnd) >= 3) {
      applyExtremes(nextStart, nextEnd);
    }
  };

  const handleZoomOut = () => {
    if (!timesMs.length || !xDomain) return;
    const fullStart = timesMs[0];
    const fullEnd = timesMs[timesMs.length - 1];
    const fullSpan = fullEnd - fullStart;

    const curStart = xDomain[0].getTime();
    const curEnd = xDomain[1].getTime();
    const curSpan = curEnd - curStart;
    const mid = (curStart + curEnd) / 2;
    const nextSpan = curSpan / 0.7;

    if (nextSpan >= fullSpan) {
      const axis = getChart()?.xAxis?.[0];
      if (axis) axis.setExtremes(null, null, true, false);
      return;
    }

    let nextStart = mid - nextSpan / 2;
    let nextEnd = mid + nextSpan / 2;
    if (nextStart < fullStart) {
      nextEnd += fullStart - nextStart;
      nextStart = fullStart;
    }
    if (nextEnd > fullEnd) {
      nextStart -= nextEnd - fullEnd;
      nextEnd = fullEnd;
    }
    applyExtremes(nextStart, nextEnd);
  };

  const handleResetZoom = () => {
    const axis = getChart()?.xAxis?.[0];
    if (axis) axis.setExtremes(null, null, true, false);
    setXDomain(null);
    setHidden(new Set());
  };

  const handleDownloadPng = () => {
    const chart = getChart();
    if (!chart) return;
    const svgEl = chart.container && chart.container.querySelector("svg");
    if (!svgEl) return;
    const cloned = svgEl.cloneNode(true);
    // inject background rect so export isn't transparent
    const bgRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bgRect.setAttribute("x", "0");
    bgRect.setAttribute("y", "0");
    bgRect.setAttribute("width", svgEl.getAttribute("width") || chart.chartWidth || 1200);
    bgRect.setAttribute("height", svgEl.getAttribute("height") || chart.chartHeight || 480);
    bgRect.setAttribute("fill", colorScheme === "dark" ? "#111111" : "#ffffff");
    cloned.insertBefore(bgRect, cloned.firstChild);
    const svgString = new XMLSerializer().serializeToString(cloned);
    const width = parseInt(svgEl.getAttribute("width"), 10) || chart.chartWidth || 1200;
    const height = parseInt(svgEl.getAttribute("height"), 10) || chart.chartHeight || 480;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        a.download = `result-series-${ts}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
  };

  const handleDownloadCsv = () => {
    try {
      if (!times.length || !series.length) return;
      const { unitLabel } = unitHelpers;

      const getResultsArrayInsensitive = (obj, key) => {
        if (!obj || !key) return null;
        if (Array.isArray(obj[key])) return obj[key];
        const lower = String(key).toLowerCase();
        const matchKey = Object.keys(obj).find((k) => String(k).toLowerCase() === lower);
        return matchKey && Array.isArray(obj[matchKey]) ? obj[matchKey] : null;
      };

      const csvOnlyCols = [
        {
          key: "purchaseCost",
          header: `Import Tariff (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "purchaseCost"),
        },
        {
          key: "sellbackCost",
          header: `Export Tariff (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "sellbackCost"),
        },
        {
          key: "penalty",
          header: `Penalty (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "penalty"),
        },
        {
          key: "gridImport",
          header: `Grid Import (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridImport"),
        },
        {
          key: "gridExport",
          header: `Grid Export (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridExport"),
        },
        {
          key: "gridExcess",
          header: `Grid Excess (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridExcess"),
        },
        {
          key: "gridUnmet",
          header: `Grid Unmet (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridUnmet"),
        },
        {
          key: "batteryCharge",
          header: `Battery Charge (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "batteryCharge")?.map((v) => unitHelpers.scaleValue(v)),
        },
        {
          key: "batteryDischarge",
          header: `Battery Discharge (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "batteryDischarge")?.map((v) => unitHelpers.scaleValue(v)),
        },
        {
          key: "batterySOC",
          header: "Battery SOC (0-1)",
          values: getResultsArrayInsensitive(results, "batterySOC"),
        },
        {
          key: "tariffEffective",
          header: `Tariff Effective (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "tariffEffective"),
        },
        {
          key: "tariffSubscribed",
          header: `Tariff Subscribed (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "tariffSubscribed"),
        },
      ].filter((c) => Array.isArray(c.values) && c.values.length);

      const csvEscape = (val) => {
        const s = String(val);
        if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };

      const rows = [];
      const headerCols = [
        "DateTime",
        ...series.map((s) => `${s.label} (${unitLabel})`),
        ...csvOnlyCols.map((c) => c.header),
      ];
      rows.push(headerCols.map(csvEscape).join(","));

      for (let i = 0; i < times.length; i++) {
        const dateStr = moment(times[i]).format("YYYY-MM-DD HH:mm:ss");
        const vals = series.map((s) => {
          const v = scaleSeriesValue(s, s.values[i]);
          return v !== undefined && v !== null ? v : "";
        });
        const csvOnlyVals = csvOnlyCols.map((c) => {
          const v = c.values?.[i];
          return v !== undefined && v !== null ? v : "";
        });
        rows.push([dateStr, ...vals, ...csvOnlyVals].join(","));
      }
      const csvContent = rows.join("\n");
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `result-series-${ts}.csv`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_err) {
      // Silent fail.
    }
  };

  return (
    <div className={`rs-wrapper ${colorScheme === "dark" ? "dark" : "light"}`}>
      <div className="rs-toolbar">
        <button
          className="rs-btn"
          aria-label={inModalInstance ? "time-series-close-fullscreen" : "time-series-open-fullscreen"}
          title={inModalInstance ? t("common.closeFullscreen") : t("common.openFullscreen")}
          onClick={() => {
            if (inModalInstance) handleCloseModal();
            else setFullscreenModal(true);
          }}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">
            {inModalInstance ? "🗗" : "⛶"}
          </span>
        </button>
        {/* <button
          className={`rs-btn rs-btn-pan ${panMode ? "is-active" : ""}`}
          aria-label={panMode ? "time-series-disable-pan" : "time-series-enable-pan"}
          title={panMode ? "Pan enabled (click to disable)" : "Pan disabled (click to enable)"}
          aria-pressed={panMode}
          onClick={() => setPanMode((p) => !p)}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">
            {panMode ? "🖐️" : "↔"}
          </span>
        </button> */}
        <button className="rs-btn" aria-label="time-series-zoom-in" title={t("common.zoomIn")} onClick={handleZoomIn}>
          +
        </button>
        <button className="rs-btn" aria-label="time-series-zoom-out" title={t("common.zoomOut")} onClick={handleZoomOut}>
          -
        </button>
        <button className="rs-btn" aria-label="time-series-reset-zoom" title={t("common.resetZoom")} onClick={handleResetZoom}>
          Reset
        </button>
        {hasPvSeries && (
          <button
            className="rs-btn"
            aria-label="time-series-transform-pv"
            title={pvTransformEnabled ? "Show signed PV values" : "Transform PV to positive"}
            aria-pressed={pvTransformEnabled}
            onClick={() => setPvTransformEnabled((prev) => !prev)}
          >
            <span role="img" aria-hidden="true" className="rs-pan-icon">{pvTransformEnabled ? "±" : "⇅"}</span>
          </button>
        )}
        <select
          className="rs-btn rs-download-select"
          value={powerUnit}
          title={t("timeSeries.unitToggle")}
          onChange={(e) => setPowerUnit(e.target.value)}
          style={{ width: 90 }}
        >
          <option value="W">W{unitSuffix}</option>
          <option value="kW">kW{unitSuffix}</option>
        </select>
        <select
          className="rs-btn rs-download-select"
          title={t("common.download")}
          value=""
          onChange={(e) => {
            const value = e.target.value;
            if (value === "png") handleDownloadPng();
            else if (value === "csv") handleDownloadCsv();
            e.target.selectedIndex = 0;
          }}
        >
          <option value="" disabled hidden>
            {t("common.download") || "Download"}
          </option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>

      <div className="rs-legend">
        {legendStats.map((s) => (
          <button
            key={s.key}
            type="button"
            style={{
              margin: "2px 10px",
              padding: "4px 10px",
              opacity: s.hidden ? "0.45" : "1",
              background: s.hidden
                ? colorScheme === "dark"
                  ? "#1e1e1e"
                  : "#f1f3f5"
                : colorScheme === "dark"
                  ? "#2e2e2e"
                  : "#ffffff",
            }}
            title={s.hidden ? `Show ${s.label}` : `Hide ${s.label}`}
            onClick={() => {
              setHidden((prev) => {
                const next = new Set(prev);
                if (next.has(s.key)) next.delete(s.key);
                else next.add(s.key);
                return next;
              });
            }}
          >
            <span
              style={{
                width: 14,
                height: 8,
                borderRadius: 2,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, color: colorScheme === "dark" ? "#d5d7e0" : "#222" }}>
              {`${s.label}: Min ${s.minText} ${s.unitLabel} , Max ${s.maxText} ${s.unitLabel}`}
            </span>
          </button>
        ))}
      </div>

      {!maxLen ? (
        <div className="indicator-downloadable rs-chart" style={{ minHeight: 260, padding: "12px 16px" }}>
          {t("common.noDataAvailable")}
        </div>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="stockChart"
          options={chartOptions}
          ref={chartRef}
          containerProps={{ className: "indicator-downloadable rs-chart" }}
        />
      )}

      <FullscreenModal
        open={fullscreenModal && !inModalInstance}
        onClose={handleCloseModal}
        title={t("result.timeSeries.title")}
      >
        <ResultSeries
          results={results}
          onFullscreen={handleCloseModal}
          inModalInstance={true}
          kpiProjectSettings={kpiProjectSettings}
          seriesConfig={seriesConfig}
          socConfig={socConfig}
          unitSuffix={unitSuffix}
        />
      </FullscreenModal>
    </div>
  );
};

export default ResultSeries;